import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useRole } from '../hooks/useRole';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Badge } from '../components/ui/Badge';
import { TableSkeleton } from '../components/ui/Skeleton';
import { formatCurrency, cn } from '../lib/utils';
import toast from 'react-hot-toast';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const LEVELS = ['Nursery 1', 'Nursery 2', 'Kindergarten 1', 'Kindergarten 2', 'Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6', 'Secondary 1', 'Secondary 2', 'Secondary 3', 'Secondary 4', 'Secondary 5'];

const EMPTY_FORM = { name: '', level: 'Primary 1', duration_hours: 1.5, rate: 0, frequency: 1, billing_cycle_type: 'per_lesson', assigned_staff_ids: [] };

export default function Admin() {
  const { profile: currentProfile, isAdmin } = useRole();
  const [data, setData] = useState({ list: [], loading: true });
  const [profiles, setProfiles] = useState([]);
  const [ui, setUi] = useState({ open: false, editId: null });
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const [{ data: res }, { data: staff }] = await Promise.all([
      supabase.from('subjects').select('*').order('name').order('level'),
      supabase.from('profiles').select('id, first_name, last_name, teacher_nickname, role, email').order('first_name'),
    ]);
    setData({ list: res || [], loading: false });
    setProfiles(staff || []);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (ui.editId) await supabase.from('subjects').update(form).eq('id', ui.editId);
    else await supabase.from('subjects').insert([form]);
    toast.success('Updated');
    setUi({ open: false, editId: null });
    load();
  };

  const remove = async (id, name) => {
    if (confirm(`Delete ${name}?`)) { await supabase.from('subjects').delete().eq('id', id); load(); }
  };

  const reset = () => { setForm(EMPTY_FORM); setUi({ open: true, editId: null }); };

  const getStaffName = (p) =>
    p.teacher_nickname || `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || 'Unknown';

  const toggleStaff = (profileId) => {
    const current = form.assigned_staff_ids || [];
    const next = current.includes(profileId)
      ? current.filter(id => id !== profileId)
      : [...current, profileId];
    setForm({ ...form, assigned_staff_ids: next });
  };

  return (
    <div className="space-y-10 pb-10 px-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold dark:text-white">Admin Center</h1>
          <p className="text-slate-500 font-medium">Academic standards and pricing</p>
        </div>
        <Button onClick={reset} className="rounded-2xl px-10 font-bold text-xs">
          <SafeIcon icon={Lu.LuPlus} className="mr-2" /> New Track
        </Button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border overflow-hidden shadow-sm">
        {data.loading ? <TableSkeleton rows={5} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b font-bold text-[11px] uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-4">Track</th>
                  <th className="px-4 py-4">Staff Access</th>
                  <th className="px-4 py-4">Defaults</th>
                  <th className="px-4 py-4">Billing</th>
                  <th className="px-4 py-4">Rate</th>
                  <th className="px-4 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {data.list
                .filter(s => {
                  const ids = s.assigned_staff_ids || [];
                  if (!ids.length || isAdmin) return true;
                  return ids.includes(currentProfile?.id);
                })
                .map(s => {
                  const assignedIds = s.assigned_staff_ids || [];
                  const assignedStaff = assignedIds
                    .map(sid => profiles.find(p => p.id === sid))
                    .filter(Boolean);
                  return (
                    <tr key={s.id} className="hover:bg-slate-50 group transition-colors">
                      <td className="px-4 py-4 font-semibold dark:text-white">
                        {s.name}
                        <Badge variant="primary" className="ml-2">{s.level}</Badge>
                      </td>
                      <td className="px-4 py-4">
                        {assignedStaff.length === 0 ? (
                          <span className="text-[10px] font-medium text-slate-400 flex items-center gap-1">
                            <SafeIcon icon={Lu.LuUsers} size={11} className="text-slate-300" />
                            All staff
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {assignedStaff.map(p => (
                              <span key={p.id} className="text-[8px] font-bold bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded-full border border-primary-100">
                                {getStaffName(p)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-slate-500">
                        <SafeIcon icon={Lu.LuClock} className="inline mr-1 text-primary-500" size={14} /> {s.duration_hours}h
                        <SafeIcon icon={Lu.LuRepeat} className="inline ml-4 mr-1 text-indigo-500" size={14} /> {s.frequency}x/wk
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={s.billing_cycle_type === 'per_lesson' ? 'success' : 'warning'}>
                          {s.billing_cycle_type.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 font-bold dark:text-white">
                        {formatCurrency(s.rate)}
                        <span className="block text-[10px] text-slate-400">
                          {s.billing_cycle_type === 'per_lesson' ? 'per lesson' : 'per hour'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setForm({ ...s, assigned_staff_ids: s.assigned_staff_ids || [] });
                              setUi({ open: true, editId: s.id });
                            }}
                            className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-primary-600 rounded-xl"
                          >
                            <SafeIcon icon={Lu.LuEdit2} size={18} />
                          </button>
                          <button
                            onClick={() => remove(s.id, s.name)}
                            className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-rose-600 rounded-xl"
                          >
                            <SafeIcon icon={Lu.LuTrash2} size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={ui.open} onClose={() => setUi({ ...ui, open: false })} title={ui.editId ? 'Edit Track' : 'New Track'} className="max-w-xl overflow-y-auto max-h-[90vh]">
        <form onSubmit={save} className="space-y-6 p-1">
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <Input label="Subject" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500">Level</label>
              <select className="w-full rounded-2xl border bg-slate-50 px-4 py-2.5 text-sm font-semibold" value={form.level} onChange={e => setForm({ ...form, level: e.target.value })}>
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500">Billing</label>
              <select className="w-full rounded-2xl border bg-slate-50 px-4 py-2.5 text-sm font-semibold" value={form.billing_cycle_type} onChange={e => setForm({ ...form, billing_cycle_type: e.target.value })}>
                <option value="per_lesson">Lesson</option>
                <option value="per_hour">Hour</option>
              </select>
            </div>
            <Input label="Rate ($)" type="number" step="0.01" value={form.rate} onChange={e => setForm({ ...form, rate: parseFloat(e.target.value) })} required />
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-500">Frequency</label>
              <select className="w-full rounded-2xl border bg-slate-50 px-4 py-2.5 text-sm font-semibold" value={form.frequency} onChange={e => setForm({ ...form, frequency: parseInt(e.target.value) })}>
                {[1, 2, 3, 4, 5, 6, 7].map(f => <option key={f} value={f}>{f}x Weekly</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Input label="Hours per Session" type="number" step="0.1" value={form.duration_hours} onChange={e => setForm({ ...form, duration_hours: parseFloat(e.target.value) })} required />
            </div>
          </div>

          {/* Staff Assignment */}
          {profiles.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-[11px] font-bold text-slate-600 block">Staff Access</label>
                  <p className="text-[9px] text-slate-400 font-medium mt-0.5">
                    Leave all unchecked = visible to every staff member.
                    Select specific staff to restrict visibility to them only.
                  </p>
                </div>
                {(form.assigned_staff_ids || []).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, assigned_staff_ids: [] })}
                    className="text-[9px] font-bold text-rose-400 hover:text-rose-600 shrink-0 ml-3"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto border rounded-2xl p-3 bg-slate-50/50">
                {profiles.map(p => {
                  const isSelected = (form.assigned_staff_ids || []).includes(p.id);
                  const name = getStaffName(p);
                  return (
                    <label
                      key={p.id}
                      className={cn(
                        'flex items-center gap-3 p-2.5 rounded-xl border-2 cursor-pointer transition-all',
                        isSelected
                          ? 'border-primary-400 bg-primary-50'
                          : 'border-slate-100 bg-white hover:border-slate-200'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleStaff(p.id)}
                        className="accent-primary-600 w-4 h-4 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-[10px] font-bold truncate', isSelected ? 'text-primary-700' : 'text-slate-700')}>
                          {name}
                        </p>
                        <p className="text-[8px] text-slate-400 font-medium capitalize">{p.role}</p>
                      </div>
                      {isSelected && <SafeIcon icon={Lu.LuCheck} size={12} className="text-primary-600 shrink-0" />}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Projection */}
          <div className="bg-slate-900 p-8 rounded-[2rem] text-white">
            <p className="text-[10px] font-bold text-slate-400">Monthly Projection</p>
            <p className="text-4xl font-bold">
              {formatCurrency(
                form.billing_cycle_type === 'per_lesson'
                  ? form.rate * form.frequency * 4
                  : form.rate * form.duration_hours * form.frequency * 4
              )}
            </p>
          </div>

          <div className="flex gap-4">
            <Button variant="ghost" onClick={() => setUi({ ...ui, open: false })} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1 font-bold">Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}